import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { vehicleService, orgService } from "@/services/api";
import { toast } from "sonner";
import { Loader2, Wand2 } from "lucide-react";

const EMPTY = { nickname: "", make: "", model: "", year: "", vin: "", licensePlate: "", facilityId: "", deviceId: "" };

// Same dialog handles both "Add Vehicle" and "Edit Vehicle" — pass a
// `vehicle` prop to edit an existing one, omit it to create a new one.
export function AddVehicleDialog({ open, onOpenChange, vehicle, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [decoding, setDecoding] = useState(false);
  const [decodedVin, setDecodedVin] = useState(null); // VIN the make/model/year were last decoded from
  const isEdit = !!vehicle;

  useEffect(() => {
    if (!open) return;
    orgService.facilities().then(setFacilities).catch(() => setFacilities([]));
    setForm(
      vehicle
        ? {
            nickname: vehicle.nickname || "",
            make: vehicle.make || "", model: vehicle.model || "", year: vehicle.year || "",
            vin: vehicle.vin || "", licensePlate: vehicle.plate || "",
            facilityId: vehicle.facilityId || "", deviceId: "",
          }
        : EMPTY
    );
    setDecodedVin(null);
  }, [open, vehicle]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // Looks the VIN up against NHTSA's public decoder and fills in
  // make/model/year — the fields a VIN actually encodes — so nobody has
  // to type them in by hand. Fires automatically once a full 17-character
  // VIN is entered, and is re-triggerable via the button for a manual retry.
  const decodeVin = async () => {
    const vin = form.vin.trim().toUpperCase();
    if (vin.length !== 17 || vin === decodedVin) return;
    setDecoding(true);
    try {
      const info = await vehicleService.decodeVin(vin);
      setForm((f) => ({
        ...f,
        vin,
        make: info.make || f.make,
        model: info.model || f.model,
        year: info.year || f.year,
      }));
      setDecodedVin(vin);
      if (info.warning) toast.warning("VIN decoded with a caveat", { description: info.warning });
      else if (info.make || info.model) toast.success(`Decoded: ${[info.year, info.make, info.model].filter(Boolean).join(" ")}`);
      else toast.info("VIN decoded, but no make/model/year came back — fill those in manually.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not decode this VIN");
    } finally {
      setDecoding(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await vehicleService.update(vehicle.id, form);
        toast.success("Vehicle updated");
      } else {
        await vehicleService.create(form);
        toast.success("Vehicle added", { description: "The new vehicle has been added to your fleet." });
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save vehicle");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-vehicle-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update this vehicle's details." : "Register a new vehicle and pair a Bouncie device."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {/* First, because it is the name this vehicle is called by
              everywhere else in the app — the fleet list, the map panel,
              reports, the trip picker. Without it those all fall back to
              "make model", so two Audi A4s become indistinguishable. */}
          <div className="space-y-1.5">
            <Label>Nickname</Label>
            <Input
              placeholder="Fleet 210"
              maxLength={64}
              value={form.nickname}
              onChange={set("nickname")}
              data-testid="vehicle-nickname"
            />
            <p className="text-xs text-muted-foreground">
              Optional. Shown throughout the app; falls back to make and model when empty.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Make</Label><Input placeholder="Audi" required value={form.make} onChange={set("make")} /></div>
            <div className="space-y-1.5"><Label>Model</Label><Input placeholder="A4" required value={form.model} onChange={set("model")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Year</Label><Input type="number" placeholder="2023" value={form.year} onChange={set("year")} /></div>
            <div className="space-y-1.5">
              <Label>VIN</Label>
              <div className="flex gap-1.5">
                <Input
                  placeholder="WAUENAF40PN012934"
                  required
                  maxLength={17}
                  value={form.vin}
                  onChange={(e) => set("vin")({ target: { value: e.target.value.toUpperCase() } })}
                  onBlur={decodeVin}
                  className="uppercase"
                />
                <Button type="button" variant="secondary" size="icon" disabled={decoding || form.vin.trim().length !== 17} onClick={decodeVin} title="Decode VIN">
                  {decoding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Enter a full 17-character VIN to auto-fill make, model and year.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>License plate</Label><Input placeholder="TX-ARC104" value={form.licensePlate} onChange={set("licensePlate")} /></div>
            <div className="space-y-1.5">
              <Label>Facility</Label>
              <Select value={form.facilityId} onValueChange={(v) => setForm((f) => ({ ...f, facilityId: v }))}>
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  {facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {!isEdit && (
            <div className="space-y-1.5">
              <Label>Bouncie device ID</Label>
              <Input placeholder="e.g. the device's serial from the Bouncie app" value={form.deviceId} onChange={set("deviceId")} />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="add-vehicle-submit">
              {saving ? (isEdit ? "Saving…" : "Adding…") : isEdit ? "Save Changes" : "Add Vehicle"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
